import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Desktop, Mobile } from '../../App';
import { useAppSelector } from '../../app/hooks';
import { AnimatedDashboard } from '../../components/animation/AnimatedDashboard';
import { AnimatedItem } from '../../components/animation/AnimatedItem';
import { SidebarModal } from '../../components/common/Sidebar/Sidebar';
import Spinner from '../../components/common/Spinner';
import {
  CLUB,
  LP,
  MIX,
  USDC,
  USDT,
  formatUSD,
  formatUSDNoDecimals
} from '../../helpers/helperFunctions';
import { getOrders } from '../../slices/orders-slice';
import { Flexed, PageTitle, Pill } from '../../styles/GlobalComponents';
import { FlexBetween, HubLengthMessage } from '../../styles/stylesStyled';
import { CurrencyType } from '../../types/types';
import {
  InvoiceHeader,
  Invoices,
  InvoicesMobile
} from '../Accounting/AccountingStyled';
import { MyOrderListModel } from '../Marketplace/Maeketplace.types';
import CancelPaymentPackage from './CancelPaymentPackage';
import WaitingForPaymentPackage from './WaitingForPaymentPackage';
import { TextWithTooltip } from '../../components/common/TextWithTooltip';
import { CaptionText, H2, P1, P3 } from '../../styles/FontStyles';
import { colors, lightTheme } from '../../styles/GlobalStyles';
import orderImage from '../../assets/images/backoffice/order.png';

export enum OrderType {
  ORDER = 1,
  PACKAGE,
  LICENCE,
  COMBO,
  EVENT_TICKET,
  DISCOUNT_CODE,
  TRAVEL_SUBSCRIPTION
}

export const Orders = () => {
  const [isSideModalOpen, setIsSideModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] =
    useState<MyOrderListModel | null>(null);
  const { t } = useTranslation('packages');
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getOrders());
  }, [dispatch]);

  const { loading, orders } = useAppSelector((state) => state.orders);

  const orderStatus = (inv: MyOrderListModel) => {
    switch (inv.status) {
      case 1:
        return (
          <>
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setIsSideModalOpen(!isSideModalOpen);
              }}>
              {t('my_hubs_part.waiting')}
            </div>

            <div style={{ cursor: 'pointer' }}>
              {inv.type !== OrderType.LICENCE &&
                inv.currencyType !== CurrencyType.MIX &&
                inv.type !== OrderType.TRAVEL_SUBSCRIPTION && (
                  <>{t('my_hubs_part.cancel')}</>
                )}
            </div>
          </>
        );

      case 2:
        return <>{t('my_hubs_part.paid')}</>;
      case 3:
        return <>{t('my_hubs_part.canceledByAdmin')}</>;
      case 4:
        return <>{t('my_hubs_part.canceled')}</>;
      default:
    }
  };

  const orderStatusColor = (inv: MyOrderListModel) => {
    switch (inv.status) {
      case 1:
        return '#FFCC4A';
      case 2:
        return '#7FB22C';
      case 3:
        return '#CD0C0C';
      case 4:
        return '#CD0C0C';
      default:
        return '#000';
    }
  };

  const currencyTypeRender = (currencyType: CurrencyType) => {
    switch (currencyType) {
      case CurrencyType.BANK_TRANSFER:
        return `${USDT} (tether)`;
      case CurrencyType.SHOPPING_POINTS:
        return 'Travel Account';
      case CurrencyType.REWARD_POINTS:
        return 'Cash Account';
      case CurrencyType.LOYALTY_POINTS:
        return 'Coin buyback';
      case CurrencyType.USDC:
        return `${USDC}`;
      case CurrencyType.MIX:
        return 'MULTIPLE';
    }
  };

  if (loading && !orders) {
    return <Spinner fullScreen />;
  }

  return (
    <AnimatedDashboard zoom dark>
      <H2 color={colors.primary900}> {t('orders')}</H2>

      <Desktop>
        <InvoiceHeader>
          <div style={{ width: '33.33%' }}>{t('my_hubs_part.hub_name')}</div>
          <div style={{ width: '16.66%' }}>
            {t('my_hubs_part.date_created')}
          </div>
          <div style={{ width: '16.66%' }}>{t('my_hubs_part.date_paid')}</div>

          <div style={{ width: '16.66%' }}>{t('my_hubs_part.value')}</div>
          <div
            style={{
              width: '16.66%'
            }}>
            {t('my_hubs_part.status')}
          </div>
        </InvoiceHeader>
      </Desktop>
      {orders?.length === 0 ? (
        <HubLengthMessage
          // onClick={() => dispatch(setSelectedTab('hub'))}
          style={{ cursor: 'pointer' }}>
          {t('my_hubs_part.no_hubs')}
        </HubLengthMessage>
      ) : (
        orders?.map((inv: MyOrderListModel, i: number) => {
          return (
            <AnimatedItem key={i} index={i}>
              <Desktop>
                <Invoices>
                  <div style={{ width: '33.33%' }}>
                    <Flexed center gap='10px'>
                      <img
                        src={orderImage}
                        alt='order'
                        style={{ width: 60, height: 60 }}
                      />
                      <div>
                        <P3 color={colors.neutral700}>#{inv.orderId}</P3>
                        <P1 semiBold>
                          {!inv?.name ? 'Free Trial Backoffice' : inv?.name}
                        </P1>
                      </div>
                    </Flexed>
                  </div>
                  <div style={{ width: '16.66%' }}>
                    <P1 semiBold>
                      {moment.utc(inv?.dateCreated).format('DD/MM/YY')}
                    </P1>
                  </div>
                  <div style={{ width: '16.66%' }}>
                    <P1 semiBold>
                      {inv?.datePaid
                        ? moment.utc(inv?.datePaid).format('DD/MM/YY')
                        : '-'}
                    </P1>
                  </div>
                  <div style={{ width: '16.66%' }}>
                    <Flexed alignCenter>
                      <P1 semiBold>
                        {CurrencyType.MIX !== inv.currencyType &&
                          formatUSDNoDecimals(inv.price)}{' '}
                        {currencyTypeRender(inv.currencyType)}
                      </P1>
                      {CurrencyType.MIX === inv.currencyType && (
                        <TextWithTooltip
                          margin='0 0 0 10px'
                          width='auto'
                          tooltip={
                            <div>
                              {inv.payments.map((payment, i) => (
                                <CaptionText
                                  color={lightTheme.colors.neutral900}
                                  key={i}>
                                  <span
                                    style={{
                                      width: 150,
                                      display: 'inline-block'
                                    }}>
                                    {payment.amount}{' '}
                                    {currencyTypeRender(payment.currencyType)}{' '}
                                  </span>
                                  {moment(payment.dateCreated).format(
                                    'YYYY-MM-DD HH:mm:ss'
                                  )}
                                </CaptionText>
                              ))}
                              {inv.amountToPay > 0 && (
                                <CaptionText
                                  color={lightTheme.colors.neutral900}
                                  key={i}>
                                  <span
                                    style={{
                                      width: 150,
                                      display: 'inline-block'
                                    }}>
                                    {formatUSD(inv.amountToPay)}{' '}
                                    {inv.currencyAmountToPay}
                                  </span>
                                  <span
                                    style={{
                                      color: lightTheme.colors.warning
                                    }}>
                                    PENDING
                                  </span>
                                </CaptionText>
                              )}
                            </div>
                          }></TextWithTooltip>
                      )}
                    </Flexed>
                  </div>
                  <div
                    style={{
                      width: '16.66%',
                      textAlign: 'right'
                    }}
                    onClick={() => setSelectedPackage(inv)}>
                    <Pill color={orderStatusColor(inv)}>
                      {orderStatus(inv)}
                    </Pill>
                  </div>
                </Invoices>
              </Desktop>
              <Mobile>
                <InvoicesMobile style={{ cursor: 'pointer' }}>
                  <FlexBetween>
                    <div
                      style={{
                        fontWeight: 500,
                        lineHeight: '20px'
                      }}>
                      {!inv?.name ? (
                        'Free Trial Backoffice'
                      ) : (
                        <>
                          {inv.name.includes('+') ? (
                            <>
                              {inv.name.substring(0, inv.name.indexOf('+') - 1)}
                              <br />
                              {inv.name.substring(inv.name.indexOf('+') - 1)}
                            </>
                          ) : (
                            inv.name
                          )}
                        </>
                      )}
                    </div>
                    <div
                      style={{
                        fontWeight: 500,
                        lineHeight: '20px',
                        fontSize: 18
                      }}>
                      <Flexed alignCenter>
                        <b>
                          <span>
                            {CurrencyType.MIX !== inv.currencyType &&
                              formatUSDNoDecimals(inv.price)}{' '}
                            {currencyTypeRender(inv.currencyType)}
                          </span>
                        </b>
                        {CurrencyType.MIX === inv.currencyType && (
                          <TextWithTooltip
                            margin='0 0 0 10px'
                            width='auto'
                            tooltip={
                              <div>
                                {inv.payments.map((payment, i) => (
                                  <CaptionText
                                    color={lightTheme.colors.neutral900}
                                    key={i}>
                                    <span
                                      style={{
                                        width: 150,
                                        display: 'inline-block'
                                      }}>
                                      {payment.amount}{' '}
                                      {currencyTypeRender(payment.currencyType)}{' '}
                                    </span>
                                    {moment(payment.dateCreated).format(
                                      'YYYY-MM-DD HH:mm:ss'
                                    )}
                                  </CaptionText>
                                ))}
                                {inv.amountToPay > 0 && (
                                  <CaptionText
                                    color={lightTheme.colors.neutral900}
                                    key={i}>
                                    <span
                                      style={{
                                        width: 150,
                                        display: 'inline-block'
                                      }}>
                                      {formatUSD(inv.amountToPay)} USDT (Tether){' '}
                                    </span>
                                    <span
                                      style={{
                                        color: lightTheme.colors.warning
                                      }}>
                                      PENDING
                                    </span>
                                  </CaptionText>
                                )}
                              </div>
                            }></TextWithTooltip>
                        )}
                      </Flexed>
                    </div>
                  </FlexBetween>
                  <FlexBetween>
                    <div>
                      {moment.utc(inv?.dateCreated).format('DD/MM/YY')}{' '}
                      <b style={{ marginLeft: '5px' }}>
                        <span>
                          {t('my_hubs_part.order_id')} {inv?.orderId}
                        </span>
                      </b>
                    </div>
                    <div
                      style={{
                        lineHeight: '18px',
                        fontSize: 12
                      }}
                      onClick={() => setSelectedPackage(inv)}>
                      <Pill color={orderStatusColor(inv)}>
                        {orderStatus(inv)}
                      </Pill>
                    </div>
                  </FlexBetween>
                </InvoicesMobile>
              </Mobile>
            </AnimatedItem>
          );
        })
      )}

      <SidebarModal
        isOpen={isSideModalOpen}
        setOpen={setIsSideModalOpen}
        title={t('my_hubs_part.payment_instructions_title')}
        sidebarWidth={'511px'}>
        <WaitingForPaymentPackage selectedPackage={selectedPackage} />
      </SidebarModal>

      <SidebarModal
        isOpen={isCancelModalOpen}
        setOpen={setIsCancelModalOpen}
        title={t('my_hubs_part.cancel_order_title')}
        sidebarWidth={'511px'}>
        <CancelPaymentPackage
          setIsCancelModalOpen={setIsCancelModalOpen}
          selectedPackage={selectedPackage}
        />
      </SidebarModal>
    </AnimatedDashboard>
  );
};
